import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  AddCountExtra,
  ClassifyLvaCountLine,
  CreateLvaCountRound,
  LvaCountLineListRow,
  LvaCountRound,
  LvaCountRoundListRow,
} from '@dcc-erp/shared';

import { api, apiJson, withSodOverride } from '../api';

// ============================================================
// LVA Phase C — physical-count rounds. Mirrors lib/queries/lva.ts (api/apiJson,
// companyId-scoped cache keys) and the cip.ts engine-decision pattern
// (apiJson + withSodOverride for the doc-type-agnostic { step, comment?|reason }
// engine bodies). The round close (final-step approval) auto-applies the line
// resolutions (MISSING→LOST etc.) + posts movements, so the decision hooks also
// invalidate ['lva-items'] (and the movement stream) on success.
// ============================================================

// Cache-key scopes — keyed by companyId so DCC/TTOP/RCI switches give
// independent caches (matches the lva/ar/cip convention).
const roundsKey = (companyId: string | undefined) => ['lva-count-rounds', companyId] as const;
const roundKey = (companyId: string | undefined, id: string | undefined) =>
  ['lva-count-rounds', companyId, id] as const;
const eventsKey = (companyId: string | undefined, id: string | undefined) =>
  ['lva-count-rounds', companyId, id, 'approval-events'] as const;
const exceptionsKey = (companyId: string | undefined) =>
  ['lva-count-rounds', companyId, 'exceptions'] as const;

// One round (header + lines + variance tally + materialized approval chain), as
// returned by GET :id (service.findRound spreads the round + approvals include +
// lines + tally). The engine columns mirror cip's Design-Z dual-status shape.
export interface LvaCountApprovalRow {
  stepNo: number;
  roleCode: string;
  roleLabel: string;
  approvedAt: string | null;
  approvedBy: string | null;
  comment: string | null;
}

export interface LvaCountVarianceTally {
  PENDING: number;
  FOUND: number;
  MISSING: number;
  MOVED: number;
  DAMAGED: number;
  NOT_IN_REGISTER: number;
}

export interface LvaCountRoundDetail extends LvaCountRound {
  lines: LvaCountLineListRow[];
  tally: LvaCountVarianceTally;
  approvals: LvaCountApprovalRow[];
}

// A cross-round exception row (unresolved MISSING / NOT_IN_REGISTER) joined with
// its source round header for the worklist.
export interface LvaCountExceptionRow extends LvaCountLineListRow {
  round: { roundNo: string; status: string; period: string };
}

// One append-only engine audit event (createdAt asc) for the AuditLog panel.
export interface LvaCountApprovalEvent {
  id: string;
  docType: string;
  docId: string;
  eventType: string;
  actorId: string | null;
  actorName: string | null;
  stepNo: number | null;
  comment: string | null;
  createdAt: string;
}

// Doc-type-agnostic engine decision inputs (the API reuses payreq's generic
// { step, comment? } / { step, reason } bodies — see lva-count.controller).
export interface LvaCountApprovalDecision {
  step: number;
  comment?: string;
  sodOverrideReason?: string;
}
export interface LvaCountApprovalReject {
  step: number;
  reason: string;
  sodOverrideReason?: string;
}

// ============================================================
// Reads
// ============================================================

export function useLvaCountRounds(companyId: string | undefined) {
  return useQuery({
    queryKey: roundsKey(companyId),
    queryFn: () => api<LvaCountRoundListRow[]>(`/companies/${companyId}/lva-count-rounds`),
    enabled: !!companyId,
  });
}

export function useLvaCountRound(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: roundKey(companyId, id),
    queryFn: () => api<LvaCountRoundDetail>(`/companies/${companyId}/lva-count-rounds/${id}`),
    enabled: !!companyId && !!id,
  });
}

export function useCountApprovalEvents(companyId: string | undefined, id: string | undefined) {
  return useQuery({
    queryKey: eventsKey(companyId, id),
    queryFn: () =>
      api<LvaCountApprovalEvent[]>(
        `/companies/${companyId}/lva-count-rounds/${id}/approval-events`,
      ),
    enabled: !!companyId && !!id,
  });
}

export function useLvaCountExceptions(companyId: string | undefined) {
  return useQuery({
    queryKey: exceptionsKey(companyId),
    queryFn: () =>
      api<LvaCountExceptionRow[]>(`/companies/${companyId}/lva-count-rounds/exceptions`),
    enabled: !!companyId,
  });
}

// ============================================================
// Counting mutations — create / scan / classify / add-extra. Each invalidates
// the round detail (lines/tally changed) + the rounds list (status/progress).
// ============================================================

function invalidateRound(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  id: string,
) {
  void qc.invalidateQueries({ queryKey: roundKey(companyId, id) });
  void qc.invalidateQueries({ queryKey: roundsKey(companyId) });
}

export function useCreateCountRound(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateLvaCountRound) =>
      api<LvaCountRound>(`/companies/${companyId}/lva-count-rounds`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: roundsKey(companyId) }),
  });
}

export function useScanCountLine(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, code }: { roundId: string; code: string }) =>
      apiJson<{ code: string }, LvaCountLineListRow>(
        'POST',
        `/companies/${companyId}/lva-count-rounds/${roundId}/scan`,
        { code },
      ),
    onSuccess: (_data, vars) => invalidateRound(qc, companyId, vars.roundId),
  });
}

export function useClassifyCountLine(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      lineId,
      data,
    }: {
      roundId: string;
      lineId: string;
      data: ClassifyLvaCountLine;
    }) =>
      apiJson<ClassifyLvaCountLine, LvaCountLineListRow>(
        'PATCH',
        `/companies/${companyId}/lva-count-rounds/${roundId}/lines/${lineId}`,
        data,
      ),
    onSuccess: (_data, vars) => invalidateRound(qc, companyId, vars.roundId),
  });
}

export function useAddCountExtra(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId, data }: { roundId: string; data: AddCountExtra }) =>
      apiJson<AddCountExtra, LvaCountLineListRow>(
        'POST',
        `/companies/${companyId}/lva-count-rounds/${roundId}/extras`,
        data,
      ),
    onSuccess: (_data, vars) => invalidateRound(qc, companyId, vars.roundId),
  });
}

// ============================================================
// Close flow (central engine, docType PC) — submit / approve-step / reject /
// return. The submit guard (every line classified) is enforced server-side.
// ============================================================

export function useSubmitCountRound(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roundId }: { roundId: string }) =>
      api<LvaCountRoundDetail>(`/companies/${companyId}/lva-count-rounds/${roundId}/submit`, {
        method: 'POST',
      }),
    onSuccess: (_data, vars) => invalidateRound(qc, companyId, vars.roundId),
  });
}

// Final-step approval CLOSES the round: it auto-applies the line resolutions
// (MISSING→item LOST, MOVED→relocate, DAMAGED→condition) and writes the
// movements, so invalidate the item list + movement stream as well.
function invalidateCountEngine(
  qc: ReturnType<typeof useQueryClient>,
  companyId: string | undefined,
  roundId: string,
) {
  invalidateRound(qc, companyId, roundId);
  void qc.invalidateQueries({ queryKey: eventsKey(companyId, roundId) });
  void qc.invalidateQueries({ queryKey: exceptionsKey(companyId) });
  void qc.invalidateQueries({ queryKey: ['lva-items', companyId] });
  void qc.invalidateQueries({ queryKey: ['lva-movements', companyId] });
}

export function useApproveCountStep(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      step,
      comment,
      sodOverrideReason,
    }: { roundId: string } & LvaCountApprovalDecision) =>
      apiJson<{ step: number; comment?: string }, LvaCountRoundDetail>(
        'POST',
        `/companies/${companyId}/lva-count-rounds/${roundId}/approve-step`,
        withSodOverride({ step, ...(comment !== undefined ? { comment } : {}) }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => invalidateCountEngine(qc, companyId, vars.roundId),
  });
}

export function useRejectCountRound(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      step,
      reason,
      sodOverrideReason,
    }: { roundId: string } & LvaCountApprovalReject) =>
      apiJson<{ step: number; reason: string }, LvaCountRoundDetail>(
        'POST',
        `/companies/${companyId}/lva-count-rounds/${roundId}/reject`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => invalidateCountEngine(qc, companyId, vars.roundId),
  });
}

export function useReturnCountRound(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      roundId,
      step,
      reason,
      sodOverrideReason,
    }: { roundId: string } & LvaCountApprovalReject) =>
      apiJson<{ step: number; reason: string }, LvaCountRoundDetail>(
        'POST',
        `/companies/${companyId}/lva-count-rounds/${roundId}/return`,
        withSodOverride({ step, reason }, sodOverrideReason),
      ),
    onSuccess: (_data, vars) => invalidateCountEngine(qc, companyId, vars.roundId),
  });
}
